import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UpdatedFact } from '@/components/product-insights/fab-ui';
import {
  PiDeniedPage,
  PiError,
  PiLoading,
  PiPageLayout,
  PiTabs,
} from '@/components/product-insights/pi-kit';
import { useFabFilters } from '@/components/product-insights/use-fab-data';
import { EventsBySchool } from '@/components/product-insights/events/EventsBySchool';
import {
  EventsFiltersBar,
  EventsHeadlineTiles,
} from '@/components/product-insights/events/EventsControls';
import { EventsTable } from '@/components/product-insights/events/EventsTable';
import {
  aggregateBySchool,
  applyEventsState,
  filterByProduct,
  filterByRegions,
  filterBySearch,
  hasEventsFilters,
  headlineFromEvents,
  headlineFromTotals,
  productOptions,
  readEventsState,
  resetEventsFilters,
  schoolMatches,
  type EventsTab,
  type EventsUrlState,
} from '@/components/product-insights/events/events-model';
import { EVENTS_FETCH_LIMIT, useEventsData } from '@/components/product-insights/events/use-events-data';
import { ApiError } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateOnly } from '@/lib/format';
import type { ProductInsightsEventParams } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/**
 * /product-insights/events — SEED events (in person and virtual), B2B view.
 *
 * Live read through EMERGE's `/product-insights/events` proxy (15-minute
 * server cache, nothing stored). Built on the shared Product Insights template
 * (`pi-kit`): header + facts, product nav, one toolbar, 4 tiles, tabs.
 * Two tabs, `tab=` in the URL (absent = events): Events · By school.
 *
 * - Cycles are SEED FAB's (1 Jun – 31 May), listed by `useFabFilters`; the
 *   resolved (current) cycle is the default, an absent key. Its date range is
 *   the facts line, a fact about the data. Nothing is fetched until the cycle
 *   is known, so the figures always belong to the cycle in the picker.
 * - Server-side: cycle, format, a single region. Client-side over the fetched
 *   set: several regions, products (sub-products; both multi-select CSV keys,
 *   owner 2026-10-28) and search (`q`, debounced 250 ms into the URL).
 * - Tiles: the server's totals while nothing narrows the set client-side, else
 *   the sum of the visible events (the Events table's total row is the same
 *   figure). Search narrows the tiles only on the Events tab; on By school it
 *   searches school names.
 * - A tab switch clears `q`, the per-tab search. Reset clears every filter
 *   (cycle included) and keeps the tab.
 */

const SEARCH_DEBOUNCE_MS = 250;

const TITLE = 'Events';

function errorMessage(error: unknown): string {
  return error instanceof ApiError && error.message
    ? error.message
    : 'SEED events could not be reached.';
}

export function ProductInsightsEventsPage() {
  const { ability } = useAuth();
  const canRead = ability.canRead('product-insights');
  if (!canRead) return <PiDeniedPage product="events" title={TITLE} />;
  return <EventsPageBody />;
}

/** Mounted only for a reader, so a refused account fetches nothing. */
function EventsPageBody() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(() => readEventsState(searchParams), [searchParams]);
  const tab = urlState.tab;

  /* --- debounced search ------------------------------------------------ */

  const [draftSearch, setDraftSearch] = useState(urlState.search);
  const [seenUrlSearch, setSeenUrlSearch] = useState(urlState.search);
  if (seenUrlSearch !== urlState.search) {
    setSeenUrlSearch(urlState.search);
    setDraftSearch(urlState.search);
  }

  useEffect(() => {
    if (draftSearch === urlState.search) return;
    const timer = window.setTimeout(() => {
      setSearchParams(
        (prev) => applyEventsState(prev, { ...readEventsState(prev), search: draftSearch }),
        { replace: true },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draftSearch, urlState.search, setSearchParams]);

  const writeState = useCallback(
    (next: EventsUrlState) => {
      setSearchParams((prev) => applyEventsState(prev, next), { replace: true });
    },
    [setSearchParams],
  );

  const changeTab = useCallback(
    (next: EventsTab) => {
      setDraftSearch('');
      setSearchParams(
        (prev) => applyEventsState(prev, { ...readEventsState(prev), tab: next, search: '' }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /* --- cycle ------------------------------------------------------------ */

  const filtersResult = useFabFilters(urlState.cycleId);
  const { filters } = filtersResult;
  const reloadFilters = filtersResult.reload;
  const selectedCycleId = urlState.cycleId ?? filters?.resolvedCycleId ?? null;
  const selectedCycle = useMemo(
    () => filters?.cycles.find((c) => c.id === selectedCycleId) ?? null,
    [filters, selectedCycleId],
  );

  /* --- data ------------------------------------------------------------- */

  /* Keyed by value: the parsed arrays are fresh whenever any URL key moves. */
  const regionKey = urlState.regionIds.join(',');
  const productKey = urlState.productIds.join(',');
  const regionIds = useMemo(() => (regionKey === '' ? [] : regionKey.split(',').map(Number)), [regionKey]);
  const productIds = useMemo(() => (productKey === '' ? [] : productKey.split(',').map(Number)), [productKey]);

  const params = useMemo<ProductInsightsEventParams | null>(() => {
    if (selectedCycleId === null) return null;
    const out: ProductInsightsEventParams = { cycleId: selectedCycleId };
    if (urlState.format !== 'all') out.format = urlState.format;
    // The upstream takes one region: one goes to the server, several are filtered client-side.
    const [onlyRegion] = regionIds;
    if (regionIds.length === 1 && onlyRegion !== undefined) out.regionId = onlyRegion;
    return out;
  }, [selectedCycleId, urlState.format, regionIds]);

  const [reloadToken, setReloadToken] = useState(0);
  const result = useEventsData(params, reloadToken);
  const { data } = result;

  const refresh = useCallback(() => setReloadToken((n) => n + 1), []);
  const retry = useCallback(() => {
    reloadFilters();
    setReloadToken((n) => n + 1);
  }, [reloadFilters]);

  const products = useMemo(() => productOptions(data?.events ?? []), [data]);
  const productEvents = useMemo(
    () => filterByProduct(filterByRegions(data?.events ?? [], regionIds), productIds),
    [data, regionIds, productIds],
  );
  const listedEvents = useMemo(
    () => (tab === 'events' ? filterBySearch(productEvents, draftSearch) : productEvents),
    [tab, productEvents, draftSearch],
  );
  const headline = useMemo(() => {
    if (data === null) return null;
    const narrowed =
      productIds.length > 0 || regionIds.length > 1 || (tab === 'events' && draftSearch.trim() !== '');
    return narrowed ? headlineFromEvents(listedEvents) : headlineFromTotals(data.totals);
  }, [data, productIds, regionIds, tab, draftSearch, listedEvents]);
  const allSchoolRows = useMemo(() => aggregateBySchool(productEvents), [productEvents]);
  const schoolRows = useMemo(
    () => (tab === 'schools' ? allSchoolRows.filter((r) => schoolMatches(r, draftSearch)) : allSchoolRows),
    [tab, allSchoolRows, draftSearch],
  );

  const pageError = filtersResult.error ?? result.error;
  const waiting = data === null && pageError === null;

  const tabs = useMemo(
    () =>
      [
        { key: 'events' as const, label: 'Events', count: data === null ? null : listedEvents.length },
        { key: 'schools' as const, label: 'By school', count: data === null ? null : schoolRows.length },
      ] as const,
    [data, listedEvents.length, schoolRows.length],
  );

  const facts = (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      {selectedCycle && (
        <span>
          {formatDateOnly(selectedCycle.startDate)} – {formatDateOnly(selectedCycle.endDate)}
        </span>
      )}
      {selectedCycle && <span aria-hidden="true">·</span>}
      <UpdatedFact
        fetchedAt={data?.fetchedAt ?? null}
        refreshing={result.refreshing}
        onRefresh={refresh}
      />
      {data?.truncated === true && (
        <>
          <span aria-hidden="true">·</span>
          <span>First {EVENTS_FETCH_LIMIT.toLocaleString('en-GB')} events</span>
        </>
      )}
    </span>
  );

  const filtersSet = hasEventsFilters(urlState) || draftSearch !== '';
  const clearFilters = filtersSet
    ? () => {
        setDraftSearch('');
        writeState({ ...resetEventsFilters(urlState), search: '' });
      }
    : undefined;

  return (
    <PiPageLayout product="events" title={TITLE} facts={facts}>      <EventsFiltersBar
        state={{ ...urlState, search: draftSearch }}
        cycles={filters?.cycles ?? null}
        selectedCycleId={selectedCycleId}
        resolvedCycleId={filters?.resolvedCycleId ?? null}
        regions={filters?.regions ?? []}
        products={products}
        search={draftSearch}
        searchLabel={tab === 'events' ? 'Search events' : 'Search schools'}
        onSearchChange={setDraftSearch}
        onChange={(next) => {
          setDraftSearch(next.search);
          writeState(next);
        }}
      />

      {pageError !== null && <PiError message={errorMessage(pageError)} onRetry={retry} />}

      {(pageError === null || data !== null) && <EventsHeadlineTiles headline={headline} loading={waiting} />}

      <PiTabs tabs={tabs} value={tab} onChange={changeTab} ariaLabel="Events section" />

      <div
        className={cx('min-w-0', result.refreshing && 'opacity-70')}
        aria-busy={waiting || result.refreshing || undefined}
      >
        {data === null ? (
          waiting ? (
            <PiLoading variant="table" rows={8} />
          ) : null
        ) : tab === 'events' && headline !== null ? (
          <EventsTable events={listedEvents} headline={headline} onClearFilters={clearFilters} />
        ) : (
          <EventsBySchool rows={schoolRows} onClearFilters={clearFilters} />
        )}
      </div>
    </PiPageLayout>
  );
}

export default ProductInsightsEventsPage;
